<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :embedded="embedded"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">
    <div class="h-100">

      <ReportFilterPopover
        headerTitle="Inventory Value Filter"
        :showApplyButton="true"
        :showResetButton="true"
        :appendToBody="true"
        alignment="left"
        placement="bottom-end"
        @apply="applyInventoryValueFilter"
        @reset="resetInventoryValueFilter">
        <template v-slot:popover>
          <ReportFilterGroup label="Date Filter">
            <ReportDateRangeSelect 
              :startDate="editingInventoryValueFilter.startDate"
              :endDate="editingInventoryValueFilter.endDate"
              @updateStartDate="editingInventoryValueFilter.startDate = $event"
              @updateEndDate="editingInventoryValueFilter.endDate = $event"
            />
          </ReportFilterGroup>
          <ReportFilterGroup label="Stock Filter">
            <FormInputStockCode
              v-model="editingInventoryValueFilter.stockId"
              @updateStockInfo="editingInventoryValueFilter.stockInfo = $event"
              placeholder="Stock filter"
              :dropdownAutoWidth="true"
              :appendToBody="true" />
          </ReportFilterGroup>
        </template>
        <template v-slot:filterDisplay>
          <div>
            <i class="far fa-calendar mr-2"></i>{{appliedInventoryValueFilter.startDate | formatDate}} - {{appliedInventoryValueFilter.endDate | formatDate}}
          </div>
          <div v-if="hasStockFilter">
            <i class="far fa-inventory mr-2"></i>{{appliedInventoryValueFilter.stockInfo ? appliedInventoryValueFilter.stockInfo.name : 'None'}}
          </div>
        </template>
      </ReportFilterPopover>
      
      <ChartBar
        title="Inventory Value"
        :series="inventoryValueData.series"
        :xAxis="inventoryValueData.xAxis"
        :yAxis="inventoryValueData.yAxis"
        :legend="inventoryValueData.legend"
        :options="inventoryValueData.options"
        :popupEnabled="true"
        :exportEnabled="true"
      />
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'
import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'

import ChartBar from '@/components/charts/chartBar'

export default {
  name: 'InventoryValueChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartBar,
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportFilterGroup,
    FormInputStockCode
  },
  props: {
    title: String,
    subTitle: String,
    filter: Object,
    type: String,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  data() {
    return {
      inventoryValueData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultInventoryValueFilter: {    
        startDate: moment().local().subtract(90, 'days').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString(),
        stockId: 0,
        stockInfo: {}
      },
      editingInventoryValueFilter: {},
      appliedInventoryValueFilter: {},
    }
  },
  computed: {
    isDailyChart() {
      return this.type == 'daily'
    },
    isMonthlyChart() {
      return this.type == 'monthly'
    },
    hasStockFilter() {
      return !!this.appliedInventoryValueFilter.stockId
    }
  },
  mounted() {
    if(this.isMonthlyChart)
      this.defaultInventoryValueFilter.startDate = moment().local().subtract(7, 'years').startOf('year').toDate().toISOString()

    this.editingInventoryValueFilter = _.clone(this.defaultInventoryValueFilter)
    this.appliedInventoryValueFilter = _.clone(this.defaultInventoryValueFilter)

    this.requestChartData()
  },
  watch: {
    filter: function() {
      this.requestChartData()
    }
  },
  methods: {
    applyInventoryValueFilter() {
      this.appliedInventoryValueFilter = _.clone(this.editingInventoryValueFilter)

      this.requestChartData()
    },
    resetInventoryValueFilter() {
      this.editingInventoryValueFilter = _.clone(this.defaultInventoryValueFilter)
      this.appliedInventoryValueFilter = _.clone(this.defaultInventoryValueFilter)

      this.requestChartData()
    },
    requestChartData() {
      var inventoryValueFilter = {
        type: this.type,
        startDate: this.appliedInventoryValueFilter.startDate,
        endDate: this.appliedInventoryValueFilter.endDate,
        stockId: this.appliedInventoryValueFilter.stockId,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null,
      }

      let widgetComp = this

      this.$apiService.analyticsService.getInventoryValueChartData(inventoryValueFilter)
      .then(response => {
        let chartData = response.data

        if(this.isDailyChart) {
          this.inventoryValueData.legend = {
            data: ['Stock Value']
          }
          this.inventoryValueData.xAxis = {
            exportName: 'Date',
            exportData: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM YYYY')),
            data: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM'))
          }
          this.inventoryValueData.yAxis = [
            {
              type: 'value',
              axisLabel: {
                formatter: (value) => {
                  return this.formatCurrencySuffix(value, 1);
                }
              }
            }
          ]
          this.inventoryValueData.series = []
          this.inventoryValueData.series.push({
            type: 'bar',
            name: 'Stock Value',
            data: chartData.map(chartEntry => chartEntry.totalStockValue),
            tooltip: {
              valueFormatter(value) {
                return widgetComp.formatCurrency(value);
              }
            }
          })
        }

        if(this.isMonthlyChart) {
          let monthList = []
          let seriesList = []
          let yearList = []
          for(let i=0; i<12; i++)
            monthList.push(moment().month(i).format('MMM'))

          // Format scattered data into chart data
          if(chartData.length > 0) {
            let firstYear = chartData[0].year
            let lastYear = chartData[chartData.length - 1].year

            for(let year=firstYear; year<=lastYear; year++) {
              yearList.push(`${year}`)

              let yearData = chartData.filter(stockValueInfo => stockValueInfo.year == year)
              let yearValueSeries = monthList.map((m, monthIndex) => {
                let monthInfo = yearData.find(stockValueInfo => stockValueInfo.month == monthIndex + 1)   // Note: Month is one based (ie, 1-12)
                return monthInfo ? monthInfo.totalStockValue : 0
              })

              seriesList.push({
                name: `${year}`,
                type: 'bar',
                data: yearValueSeries,
                markLine: {
                  label: {
                    formatter: (param) => {
                      return this.formatCurrency(param.value);
                    }
                  },
                  data: [
                    {
                      type: 'average',
                      name: 'Avg',
                    }
                  ]
                },
                tooltip: {
                  valueFormatter: (value) => {
                    return this.formatCurrency(value);
                  }
                }
              })
            }
          }

          this.inventoryValueData.legend = {
            data: yearList
          }
          this.inventoryValueData.xAxis = {
            type: 'category',
            data: monthList
          }
          this.inventoryValueData.yAxis = [
            {
              type: 'value',
              axisLabel: {
                formatter: (value) => {
                  return this.formatCurrencySuffix(value, 1);
                }
              }
            }
          ]
          this.inventoryValueData.series = seriesList
        }
      })
    },
  }
}
</script>